/**
 * Locale registry — the source of truth for every locale Alouvya supports.
 *
 * Activation flow (per spec §11.5):
 *  1. A locale appears in this file with `activated: false` — it can receive
 *     translator work but does NOT show up in the public language switcher.
 *  2. Translators complete the catalog; native-speaker review signs off.
 *  3. Operations flips `activated: true` here, ships a PR, and the locale
 *     becomes selectable.
 *  4. If translation quality drops < 95% in a release, ops flips back to
 *     `activated: false` and the locale falls back to en-US silently.
 *
 * `?i18n_preview=1` URL flag bypasses the activation gate for translators/PMs.
 */

// Canonical source language — never deprecated.
export const SOURCE_LOCALE = "en-US";

// Right-to-left locales — used to flip dir attribute and trigger logical
// CSS-property fallbacks.
export const RTL_LOCALES = new Set(["ar-SA", "he-IL"]);

// Locale registry. Add an entry with activated:false to expose to translators.
// Add `legacy_first_draft:true` for the locales that have un-reviewed strings
// from before iter41 — these are kept in the repo so translators don't start
// from zero, but they sit firmly behind the activation gate.
export const LOCALES = {
  // ── Source ───────────────────────────────────────────────────────────────
  "en-US": { autonym: "English (US)", english: "English (US)", activated: true },

  // ── Tier 1 (spec §11.2) ─────────────────────────────────────────────────
  "en-GB": { autonym: "English (UK)", english: "English (UK)", activated: false },
  "es-ES": { autonym: "Español (España)", english: "Spanish (Spain)", activated: true },
  "es-MX": { autonym: "Español (México)", english: "Spanish (Mexico)", activated: false },
  "fr-FR": { autonym: "Français (France)", english: "French (France)", activated: true },
  "fr-CA": { autonym: "Français (Canada)", english: "French (Canada)", activated: false },
  "de-DE": { autonym: "Deutsch", english: "German", activated: true },
  "pt-BR": { autonym: "Português (Brasil)", english: "Portuguese (Brazil)", activated: false },
  "nl-NL": { autonym: "Nederlands", english: "Dutch", activated: false, legacy_first_draft: true },
  "it-IT": { autonym: "Italiano", english: "Italian", activated: false },
  "ar-SA": { autonym: "العربية", english: "Arabic", activated: true },
  "zh-CN": { autonym: "中文 (简体)", english: "Chinese (Simplified)", activated: false },
  "da-DK": { autonym: "Dansk", english: "Danish", activated: true },
  "nb-NO": { autonym: "Norsk bokmål", english: "Norwegian Bokmål", activated: true },
  "sv-SE": { autonym: "Svenska", english: "Swedish", activated: true },

  // ── Pseudolocale (always available in dev for layout testing) ───────────
  "qps-ploc": { autonym: "[Pseudo]", english: "Pseudo-locale (dev)", activated: false, dev_only: true },
};

// The set of locales currently visible in the public switcher. ONLY change
// this after the activation runbook checklist is complete.
export const ACTIVATED_LOCALES = new Set(
  Object.entries(LOCALES)
    .filter(([, meta]) => meta.activated)
    .map(([code]) => code)
);

// Tier 1 — used by the activation status board.
export const TIER_1_LOCALES = [
  "en-GB",
  "es-ES",
  "es-MX",
  "fr-FR",
  "fr-CA",
  "de-DE",
  "pt-BR",
  "nl-NL",
  "it-IT",
  "ar-SA",
  "zh-CN",
  "nb-NO",
  "sv-SE",
  "da-DK",
];

// Recovery work order target locales (8-document spec §3.1)
export const RECOVERY_TARGETS = ["fr-FR", "es-ES", "ar-SA", "de-DE", "da-DK", "nb-NO", "sv-SE", "nl-NL"];

/** Display label for the autonym + region tag: "Español (España)". */
export function getLocaleLabel(code) {
  return LOCALES[code]?.autonym || code;
}

/** Two-letter language stem for legacy callers ("en-US" → "en"). */
export function getLanguage(code) {
  return (code || "").split("-")[0];
}
